import { Callout, mergeStyles, Stack, Text, ThemeProvider, useTheme } from '@fluentui/react';
import { useBoolean, useId } from '@fluentui/react-hooks';
import React from 'react';
import { StateMachine } from 'xstate';
import { createModel } from 'xstate/lib/model';
import GenericLogo from '../../assets/zoryve-logo-generic-v2.png';
import TtIcon from '../../assets/tt-icon.png';
import HelpIcon from '../../assets/help-bubble-icon.png';
import Logo from '../../assets/zoryve-logo.png';
import { FlowRunner } from '../../FlowRunner';
import { GLOBALS } from '../../globals';
import { BaseContext, BaseEvent, BaseResponse } from '../../machine';
import { setAppFavicon, setAppTheme, setAppTitle } from '../utils';
import {
  BENEFIT_MESSAGES,
  ZoryveDirectBenefitReminderFlow,
} from './reminders/ZoryveDirect.BenefitReminder.flow';
import {
  REFILL_MESSAGES,
  ZoryveDirectRefillReminderFlow,
} from './reminders/ZoryveDirect.RefillReminder.flow';
import {
  RETRIEVE_FLOW_MESSAGES,
  ZoryveDirectRetrieveFlow,
} from './reminders/ZoryveDirect.Retrieve.flow';
import {
  SAVINGS_LIMIT_MESSAGES,
  ZoryveDirectLimitReminderFlow,
} from './reminders/ZoryveDirect.SavingsLimit.flow';
import { TERMS_MESSAGES, ZoryveDirectTermsFlow } from './reminders/ZoryveDirect.Terms.flow';
import { WELCOME_MESSAGES, ZoryveDirectWelcomeFlow } from './reminders/ZoryveDirect.Welcome.flow';
import {
  WELCOME_REMINDER_MESSAGES,
  ZoryveDirectWelcomeReminderFlow,
} from './reminders/ZoryveDirect.WelcomeReminder.flow';
import { YEAR_END_MESSAGES, ZoryveDirectYearEndFlow } from './reminders/ZoryveDirect.YearEnd.flow';
import * as CONFIG from './ZoryveDirect.config';
import { REMOTE_MESSAGES, ZoryveDirectFlow } from './ZoryveDirect.flow';
import { ZoryveDirectTheme } from './ZoryveDirect.theme';
import { HistoryRenderer, HistoryResponse, syncHistory } from '../../History';
import { onSubmitSync } from '../../sync';

console.log('Config', JSON.stringify(CONFIG));

const APP_BACKGROUND = '#D5DDE0';

setAppTitle('ZORYVE Direct Program V2');
setAppFavicon(Logo);
setAppTheme(APP_BACKGROUND);

const DEFAULT_FONT_SIZE = 16;

export const ZoryveDirectApp = (props: {
  history: HistoryResponse;
  initResponse: { keyword: string; metadata: Record<string, string> };
}): JSX.Element => {
  const { history, initResponse } = props;
  const [fontSize, setFontSize] = React.useState(DEFAULT_FONT_SIZE);
  const [INB, setINB] = React.useState('US-COM-PTF-01065 05/26');

  const zoryveDirectTheme = {
    ...ZoryveDirectTheme,
    fonts: {
      ...ZoryveDirectTheme.fonts,
      medium: {
        fontSize: fontSize,
      },
    },
  };

  GLOBALS.initResponse = initResponse;

  const responses = React.useMemo(() => {
    return history.inputs.reduce((acc: { [k: string]: BaseResponse }, input) => {
      acc[input.inputState] = { data: input.data };
      return acc;
    }, {});
  }, [history.inputs]);

  console.log('MODEL CONTEXT.RESPONSES', responses);

  const model = React.useMemo(
    () =>
      createModel<BaseContext, BaseEvent>({
        input_state: '',
        component: undefined,
        componentInputs: [],
        responses: responses,
        loading: false,
      }),
    [responses]
  );

  // IMPORTANT TO ENSURE THAT LAST STATE IN THE HISTORY IS RENDERED BY THE FLOWRUNNER
  const historyToRender = history.history;
  const initalState = historyToRender[historyToRender.length - 1]?.stateName;
  const [stateMachine, setStateMachine] = React.useState<
    StateMachine<
      BaseContext,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      BaseEvent,
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: any;
        context: BaseContext;
      }
    >
  >();
  const [remoteMessages, setRemoteMessages] =
    React.useState<Record<string, string | JSX.Element>>();

  React.useEffect(() => {
    switch (initResponse.keyword.toLowerCase()) {
    case 'card':
      setStateMachine(ZoryveDirectRetrieveFlow(model, initalState));
      setRemoteMessages(RETRIEVE_FLOW_MESSAGES);
      setINB('US-COM-PTF-00005 v2.0 07/24');
      break;
    case 'terms':
      setStateMachine(ZoryveDirectTermsFlow(model, initalState));
      setRemoteMessages(TERMS_MESSAGES);
      setINB('US-COM-PTF-01060 05/26');
      break;
    case 'no_longer_eligible':
      setStateMachine(ZoryveDirectTermsFlow(model, initalState));
      setRemoteMessages(TERMS_MESSAGES);
      setINB('US-COM-PTF-00007 v2.0 07/24');
      break;
    case 'welcome':
      setStateMachine(ZoryveDirectWelcomeFlow(model, initalState));
      setRemoteMessages(WELCOME_MESSAGES);
      setINB('US-COM-PTF-01066 05/26');
      break;
    case 'welcome_reminder':
      setStateMachine(ZoryveDirectWelcomeReminderFlow(model, initalState));
      setRemoteMessages(WELCOME_REMINDER_MESSAGES);
      setINB('US-COM-PTF-01062 05/26');
      break;
    case 'refill_reminder':
      setStateMachine(ZoryveDirectRefillReminderFlow(model, initalState));
      setRemoteMessages(REFILL_MESSAGES);
      setINB('US-COM-PTF-01063 05/26');
      break;
    case 'additional_benefit':
      setStateMachine(ZoryveDirectBenefitReminderFlow(model, initalState));
      setRemoteMessages(BENEFIT_MESSAGES);
      setINB('US-COM-PTF-01064 05/26');
      break;
    case 'approaching_limit':
      setStateMachine(ZoryveDirectLimitReminderFlow(model, initalState));
      setRemoteMessages(SAVINGS_LIMIT_MESSAGES);
      setINB('US-COM-PTF-01067 05/26');
      break;
    case 'year_end':
      setStateMachine(ZoryveDirectYearEndFlow(model, initalState));
      setRemoteMessages(YEAR_END_MESSAGES);
      setINB('US-COM-PTF-01068 05/26');
      break;
    default:
      setStateMachine(ZoryveDirectFlow(model, initalState));
      setRemoteMessages(REMOTE_MESSAGES);
      setINB('US-COM-PTF-01065 05/26');
      break;
    }
  }, [initResponse.keyword]);

  return (
    <ThemeProvider
      theme={zoryveDirectTheme}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        background: APP_BACKGROUND,
      }}
    >
      <Stack
        horizontalAlign='center'
        style={{
          padding: '1.5rem',
          paddingBottom: '2rem',
          height: '100%',
          background: APP_BACKGROUND,
        }}
      >
        <img
          src={GenericLogo}
          alt="ZORYVE (roflumilast) cream"
          style={{ width: '60%', padding: '1rem 0 2rem 0' }}
        />
        {remoteMessages && stateMachine && (
          <>
            <HistoryRenderer
              history={historyToRender}
              remoteMessages={remoteMessages}
            />
            <FlowRunner
              stateMachine={stateMachine}
              onSubmit={onSubmitSync}
              onSyncHistory={syncHistory}
            />
          </>
        )}
      </Stack>
      <FloatingINB INB={INB} />
      <FontSize onChangeSize={setFontSize} />
      <HelpBubble />
    </ThemeProvider>
  );
};

export interface FontSizeProps {
  onChangeSize: (size: number) => void;
}

export const FontSize = (props: FontSizeProps): JSX.Element => {
  const theme = useTheme();
  const { onChangeSize } = props;
  const fontSizes = [16, 18, 20];
  const [currentFont, setCurrentFont] = React.useState(DEFAULT_FONT_SIZE);

  const toggleFont = () => {
    const currentIndex = fontSizes.indexOf(currentFont);
    const nextFont = (currentIndex + 1) % fontSizes.length;
    setCurrentFont(fontSizes[nextFont]);
    onChangeSize(fontSizes[nextFont]);
  };

  return (
    <div
      style={{
        zIndex: 10,
        position: 'fixed',
        bottom: '2rem',
        right: '6rem',
      }}
    >
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        style={{
          background: '#545C64',
          width: '4rem',
          height: '4rem',
          borderRadius: '50%',
          boxShadow: theme.effects.elevation8,
          cursor: 'pointer',
        }}
        onClick={() => toggleFont()}
      >
        <img src={TtIcon} width="30px" />
      </Stack>
    </div>
  );
};

export const HelpBubble = (): JSX.Element => {
  const theme = useTheme();
  const [isCalloutVisible, { toggle: toggleIsCalloutVisible }] = useBoolean(false);
  const buttonId = useId('callout-button');

  const callout = (
    <Callout
      className={mergeStyles({
        width: '75%',
        background: '#545C64',
        borderRadius: '1rem',
      })}
      styles={{
        root: {
          borderRadius: '1rem',
        },
        beakCurtain: {
          borderRadius: '1rem',
        },
        calloutMain: {
          background: '#545C64',
          padding: '1rem',
          borderRadius: '1rem',
        },
        beak: {
          background: '#545C64',
        },
      }}
      gapSpace={0}
      target={`#${buttonId}`}
      onDismiss={toggleIsCalloutVisible}
      setInitialFocus
    >
      <Text block variant="medium" style={{ color: 'white' }}>
        Questions? Call{' '}
        <span>
          <a href="tel:1-844-979-1288" style={{ color: 'inherit' }}>
            <u>1-844-979-1288</u>
          </a>
        </span>{' '}
        from 6:00 am–5:00 pm PT, M–F. <br />
        Text STOP to unsubscribe. Text TERMS for Terms and Conditions to 1-440-496-7983.
      </Text>
    </Callout>
  );

  return (
    <>
      {isCalloutVisible && callout}
      <div
        id={buttonId}
        style={{
          zIndex: 10,
          position: 'fixed',
          bottom: '2rem',
          right: '1rem',
        }}
      >
        <Stack
          horizontalAlign="center"
          verticalAlign="center"
          style={{
            background: '#545C64',
            width: '4rem',
            height: '4rem',
            borderRadius: '50%',
            boxShadow: theme.effects.elevation8,
            cursor: 'pointer',
          }}
          onClick={toggleIsCalloutVisible}
        >
          <img src={HelpIcon} width="26px" />
        </Stack>
      </div>
    </>
  );
};

const FloatingINB = (props: { INB: string }): JSX.Element => {
  return (
    <div style={{ position: 'fixed', bottom: '0.5rem', left: '0.5rem' }}>
      <Text style={{ color: 'black', fontSize: 14 }}>{props.INB}</Text>
    </div>
  );
};
